import { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { showNotification } from '@epi-actions/overlays';
import {
  useCheckCompanyBeneficiarySSNMutation,
  useCreateCompanyBeneficiaryMutation,
  useLazyGetBeneficiaryDetailsQuery,
  useUpdateCompanyBeneficiaryMutation
} from '@epi-api/companyBeneficiaries';
import {
  EmployeeTypeModes,
  FIELDS_TO_WATCH,
  UNIQUE
} from '@epi-constants/beneficiary';
import { FormFields } from '@epi-forms/helpers';
import {
  addBenefitsDataMapper,
  benefitsDataMapper
} from '@epi-helpers/BeneficiaryHelper';
import { isEmptyObj } from '@epi-helpers/isEmptyObj';
import { BeneficiaryFormValues } from '@epi-models/pages/Beneficiaries';
import { getBeneficiaryPayload } from '@epi-pages/Beneficiaries/helpers/getBeneficiaryPayload';
import { isActionResponseError } from '@epi-helpers/ErrorDataHelper';
import { useGetBenefitGroupsQuery } from '@epi-api/benefitGroups';
import { LUNCH } from '@epi-constants/benefitTypes';

type OwnProps = {
  beneficiaryId: string | null;
  onClose: () => void;
  isShowModal: boolean;
  setIsEmployeeAdded: (isEmployeeAdded: boolean) => void;
};

export const useBeneficiaryForm = ({
  beneficiaryId,
  onClose,
  isShowModal,
  setIsEmployeeAdded
}: OwnProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [createBeneficiary, createState] =
    useCreateCompanyBeneficiaryMutation();
  const [updateBeneficiary, updateState] =
    useUpdateCompanyBeneficiaryMutation();
  const [getCompanyBeneficiarySSN, ssnState] =
    useCheckCompanyBeneficiarySSNMutation();
  const [triggerGetCompanybeneficiaryDetails, beneficiaryDetailsState] =
    useLazyGetBeneficiaryDetailsQuery();
  const { data: benefitGroups } = useGetBenefitGroupsQuery();
  const isLunchBenefitActive = benefitGroups?.some(s =>
    s.benefitSettings.find(
      x => x.benefitType.toLowerCase() === LUNCH.toLowerCase()
    )
  );
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [addAnotherEmployee, setAddAnotherEmployee] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isProcessing =
    createState.isLoading || updateState.isLoading || ssnState.isLoading;
  const defaultValues = beneficiaryDetailsState.data
    ? benefitsDataMapper(
        beneficiaryDetailsState.data,
        true,
        isLunchBenefitActive
      )
    : addBenefitsDataMapper(isLunchBenefitActive);

  const form = useForm<BeneficiaryFormValues>({
    mode: 'onChange',
    defaultValues
  });

  const {
    reset,
    watch,
    formState: { errors, dirtyFields },
    getValues,
    setValue
  } = form;

  const employeeType = watch(FormFields.EmployeeType);
  const daysForLunari = watch(FormFields.DaysForLunari);
  const groupid = watch(FormFields.GroupId);
  const isEmptyField = watch(FIELDS_TO_WATCH);
  const isPartTimeOrTemporaryType =
    employeeType === EmployeeTypeModes.PartTime ||
    employeeType === EmployeeTypeModes.TemporaryEmployed;

  const showNotificationMessage = useCallback(
    (message: string, isSuccess?: boolean) => {
      dispatch(showNotification({ message }, isSuccess ? 'success' : 'error'));
    },
    [dispatch]
  );

  const resetToEmpty = useCallback(() => {
    reset(addBenefitsDataMapper(isLunchBenefitActive));
  }, [isLunchBenefitActive]);

  const toggleAddAnotherEmployee = useCallback(() => {
    setAddAnotherEmployee(prev => !prev);
  }, []);

  const clearBeneficiaryDetails = useCallback(() => {
    triggerGetCompanybeneficiaryDetails('');
  }, [triggerGetCompanybeneficiaryDetails]);

  useEffect(() => {
    if (isSubmitted) return;
    if (beneficiaryDetailsState.data && beneficiaryId) {
      const mapped = benefitsDataMapper(
        beneficiaryDetailsState.data,
        true,
        isLunchBenefitActive
      );
      reset(mapped);
    } else if (!beneficiaryId) {
      resetToEmpty();
    }
  }, [beneficiaryDetailsState.data, beneficiaryId, isLunchBenefitActive]);

  useEffect(() => {
    if (beneficiaryId) {
      triggerGetCompanybeneficiaryDetails(beneficiaryId);
    }
  }, [beneficiaryId]);

  const handleUniqueError = useCallback(
    (error: any): boolean => {
      const errors = error?.content?.errors;
      const isUniqueEmployeeNumberError = errors?.some(
        (e: any) => e.message === UNIQUE
      );

      if (isUniqueEmployeeNumberError) {
        form.setError(FormFields.EmployeeNumber, {
          type: 'custom',
          message: t('messages.employee_number')
        });
      }

      return isUniqueEmployeeNumberError;
    },
    [form, t]
  );

  useEffect(() => {
    const error = createState.error || updateState.error;
    if (error && !handleUniqueError(error)) {
      showNotificationMessage('messages.error');
    }
  }, [createState.error, updateState.error]);

  const submit = useCallback(async () => {
    setIsSubmitted(true);
    const payload = getBeneficiaryPayload(getValues());
    if (beneficiaryId) {
      await updateBeneficiary(payload);
      return;
    }

    try {
      await getCompanyBeneficiarySSN(payload.personalId).unwrap();
      await createBeneficiary(payload).unwrap();
    } catch (error) {
      if (isActionResponseError(error)) {
        showNotificationMessage(`messages.${error.data.errors[0].code}`);
      } else {
        showNotificationMessage('messages.error');
      }
    }
  }, [beneficiaryId]);

  useEffect(() => {
    if (updateState.isSuccess) {
      showNotificationMessage('messages.successful_edit_employee', true);
      setIsSubmitted(false);
      resetToEmpty();
      onClose();
      clearBeneficiaryDetails();
    }
  }, [updateState.isSuccess]);

  useEffect(() => {
    if (createState.isSuccess) {
      showNotificationMessage('messages.successful_add_employee', true);
      setIsEmployeeAdded(true);
      if (addAnotherEmployee) {
        resetToEmpty();
        setAddAnotherEmployee(false);
        return;
      }
      setIsSubmitted(false);
      resetToEmpty();
      onClose();
    }
  }, [createState.isSuccess]);

  useEffect(() => {
    if (isShowModal) setIsSubmitted(false);
  }, [isShowModal]);

  useEffect(() => {
    if (
      !beneficiaryId &&
      benefitGroups?.length === 1 &&
      !groupid &&
      isShowModal
    ) {
      setValue(FormFields.GroupId, benefitGroups[0].groupId, {
        shouldValidate: true
      });
    }
  }, [benefitGroups, beneficiaryId, isShowModal]);

  const disableSubmit = (() => {
    const {
      [FormFields.EmployeePercentage]: _,
      [FormFields.DaysForLunari]: __,
      ...rest
    } = errors;
    const specificErrors = isPartTimeOrTemporaryType ? errors : rest;

    const hasErrors = !isEmptyObj(specificErrors);
    const isMissingPercentage = !watch(FormFields.EmployeePercentage);
    const isInvalidDays =
      daysForLunari && (daysForLunari < 1 || daysForLunari > 23);

    if (beneficiaryId) {
      return (
        hasErrors ||
        isMissingPercentage ||
        isInvalidDays ||
        !groupid ||
        isEmptyObj(dirtyFields)
      );
    }

    return (
      hasErrors ||
      isMissingPercentage ||
      isInvalidDays ||
      isEmptyObj(dirtyFields) ||
      isProcessing ||
      !groupid ||
      isEmptyField.includes('')
    );
  })();

  const handleClose = useCallback(() => {
    if (isEmptyObj(errors) && isEmptyObj(dirtyFields)) {
      resetToEmpty();
      clearBeneficiaryDetails();
      setShowCloseConfirmation(false);
      onClose();
      return;
    }
    setShowCloseConfirmation(true);
  }, [errors, dirtyFields, resetToEmpty, clearBeneficiaryDetails, onClose]);

  const confirmCloseWithoutSaving = useCallback(
    (isCancel?: boolean) => {
      setShowCloseConfirmation(false);
      if (isCancel) {
        resetToEmpty();
        clearBeneficiaryDetails();
        onClose();
      }
    },
    [resetToEmpty, onClose, clearBeneficiaryDetails]
  );

  const confirmSaving = useCallback(async () => {
    setShowCloseConfirmation(false);
    await submit();
  }, [submit]);

  return {
    form,
    submit,
    disableSubmit,
    isProcessing,
    handleClose,
    showCloseConfirmation,
    confirmCloseWithoutSaving,
    confirmSaving,
    addAnotherEmployee,
    toggleAddAnotherEmployee
  };
};
